import type { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fireOperationalAnalytics } from '@atlassian/analytics-bridge/fire-analytics/fire-analytics';
import type { usePfErrorUtils } from '@atlassian/proforma-common-core/error-utils-context';
import type { AsyncFieldConfiguration } from '@atlassian/proforma-form-renderer/async-fields';
import { Action, ActionSubject } from '@atlassian/proforma-utils/analytics/events';

/**
 * Wraps an AsyncFieldConfiguration["loadOptions"] to fire an operational analytic on success
 * and report errors via errorUtils on failure.
 *
 * Mirrors the legacy analytics in loadChoicesAsyncFn.tsx from proforma-common-core.
 */
export const withLoadOptionsAnalytics = (
	config: AsyncFieldConfiguration,
	schemaKey: string | undefined,
	createAnalyticsEvent: ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'],
	errorUtils: ReturnType<typeof usePfErrorUtils>,
): AsyncFieldConfiguration => {
	const originalLoadOptions = config.loadOptions;
	if (!originalLoadOptions) {
		return config;
	}

	return {
		...config,
		loadOptions: async (...args) => {
			try {
				const result = await originalLoadOptions(...args);
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					`${ActionSubject.SEARCH_ASYNC_CHOICES} ${Action.FINISHED}`,
					{ schemaKey },
				);
				return result;
			} catch (error) {
				errorUtils.reportError(error as Error, {
					id: ActionSubject.SEARCH_ASYNC_CHOICES,
					attributes: { schemaKey },
				});
				throw error;
			}
		},
	};
};
